import { useNavigate, useRouterState } from "@tanstack/react-router";
import { MapPin, Building2, Tag, Wallet, Search } from "lucide-react";
import { useState } from "react";
import { LOCATIONS, FACILITIES, PURPOSES, PRICE_RANGES } from "@/data/venues";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ANY = "__any__";

interface Field {
  icon: React.ElementType;
  label: string;
  value: string;
  setValue: (v: string) => void;
  options: readonly string[];
}

export function SearchBar({ compact = false }: { compact?: boolean }) {
  const [location, setLocation] = useState("");
  const [facility, setFacility] = useState("");
  const [purpose, setPurpose] = useState("");
  const [price, setPrice] = useState("");
  const navigate = useNavigate();
  const { location: routerLocation } = useRouterState();

  const fields: Field[] = [
    { icon: MapPin, label: "Location", value: location, setValue: setLocation, options: LOCATIONS },
    { icon: Building2, label: "Facility", value: facility, setValue: setFacility, options: FACILITIES },
    { icon: Tag, label: "Purpose", value: purpose, setValue: setPurpose, options: PURPOSES },
    { icon: Wallet, label: "Price", value: price, setValue: setPrice, options: PRICE_RANGES.map((r) => r.label) },
  ];

  const onSearch = () => {
    const params = new URLSearchParams();
    if (location) params.set("location", location);
    if (facility) params.set("facility", facility);
    if (purpose) params.set("purpose", purpose);
    if (price) params.set("price", price);

    const currentPath = routerLocation.pathname;
    let target = "/hotels";

    // Map facility to the correct category page
    if (facility === "Conference Hall") {
      target = "/event-centres";
    } else if (facility === "Beach House") {
      target = "/open-spaces";
    } else if (!facility) {
      // If no facility is picked, try to stay on current listing page if applicable
      if (["/hotels", "/event-centres", "/open-spaces"].includes(currentPath)) {
        target = currentPath;
      } else if (purpose === "Event" || purpose === "Business") {
        target = "/event-centres";
      } else if (purpose === "Retreat") {
        target = "/open-spaces";
      }
    }

    navigate({
      to: target as string,
      search: Object.fromEntries(params) as Record<string, string>,
    });
  };

  return (
    <div
      className={`relative w-full rounded-2xl border border-border/60 bg-card/95 p-2 shadow-elegant backdrop-blur ${
        compact ? "" : "lg:p-3"
      } lg:rounded-full`}
    >
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_auto]">
        {fields.map((f) => (
          <Select
            key={f.label}
            value={f.value || ANY}
            onValueChange={(v) => f.setValue(v === ANY ? "" : v)}
          >
            <SelectTrigger className="h-auto w-full justify-between gap-2 rounded-xl border-none bg-transparent px-3 py-2.5 shadow-none ring-0 hover:bg-accent/60 focus:ring-0 focus:ring-offset-0 lg:rounded-full lg:px-4 lg:py-3">
              {/* Rendered as a div, not a span — SelectTrigger applies
                  `[&>span]:line-clamp-1` to its direct child, which would
                  force `display:-webkit-box` here and break this row's
                  flex layout (icon would no longer sit beside the text). */}
              <div className="flex min-w-0 items-center gap-3">
                <f.icon className="size-4 shrink-0 text-primary" />
                <span className="flex min-w-0 flex-col items-start">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {f.label}
                  </span>
                  <SelectValue
                    placeholder={`Any ${f.label.toLowerCase()}`}
                    className="text-sm font-medium text-foreground"
                  />
                </span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any {f.label.toLowerCase()}</SelectItem>
              {f.options.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        <Button size="lg" onClick={onSearch} className="h-12 w-full rounded-xl px-6 sm:col-span-2 lg:col-auto lg:h-full lg:min-h-12 lg:w-auto lg:rounded-full">
          <Search className="size-4" />
          <span>Search</span>
        </Button>
      </div>
    </div>
  );
}
